<template>
  <div class="top3 findingo-top3 p-4" v-if="topCards.length">
    <section class="findingo-results-hero">
      <div class="findingo-results-hero-content">
        <h2 class="findingo-results-title">
          Deine passenden Heißluftfritteusen-Empfehlungen
        </h2>

        <p class="findingo-results-subtitle">
          Basierend auf deinen Antworten haben wir {{ topCards.length }} besonders spannende Modelle für dich ausgewählt.
          Weiter unten findest du weitere Heißluftfritteusen, die deine Anforderungen ebenfalls erfüllen.
        </p>
      </div>
    </section>

    <div class="row g-3 g-xl-4 align-items-start">
      <div class="col-12 col-xxl-9">
        <div class="findingo-grid">
          <div
            v-for="card in topCards"
            :key="getCardInstanceId(card)"
            class="findingo-grid-item"
          >
            <article
              class="findingo-product-card"
              :class="[
                'findingo-product-card--' + card.type,
                { 'findingo-is-flipped': isTopCardFlipped(card.type) }
              ]"
            >
              <div class="findingo-card-front">
                <div
                  class="findingo-badge"
                  :class="[
                    'findingo-badge--' + card.type,
                    { 'findingo-badge--combined': card.badges && card.badges.length > 1 }
                  ]"
                >
                  <template v-if="card.badges && card.badges.length > 1">
                    <span class="findingo-badge__icon findingo-badge__icon--combined">
                      <i class="fa-regular fa-star"></i>
                    </span>

                    <span class="findingo-badge__content">
                      <span class="findingo-badge__headline">
                        {{ getCombinedBadgeTitle(card.badges) }}
                      </span>

                      <span class="findingo-badge__subline">
                        <span
                          v-for="badge in card.badges"
                          :key="badge.type"
                          class="findingo-badge__subitem"
                        >
                          {{ getBadgeShortLabel(badge.type) }}
                        </span>
                      </span>
                    </span>
                  </template>

                  <template v-else>
                    <span class="findingo-badge__icon">
                      <i :class="card.badges[0].icon"></i>
                    </span>

                    <span class="findingo-badge__text">
                      {{ card.badges[0].ribbon }}
                    </span>
                  </template>
                </div>

                <div class="findingo-card-inner">
                  <div class="findingo-card-media">
                    <pic-carousel
                      :key="'carousel-' + getCardInstanceId(card)"
                      :product="card.product"
                      :instance-id="'carousel-' + getCardInstanceId(card)"
                    />
                  </div>

                  <div class="findingo-card-main">
                    <div class="findingo-card-info-layout">
                      <div class="findingo-card-specs">
                        <div
                          v-for="spec in getTopSpecs(card.product)"
                          :key="spec.label"
                          class="findingo-card-spec"
                        >
                          <span>
                            <small>{{ spec.label }}</small>
                            <strong>{{ spec.value }}</strong>
                          </span>
                        </div>
                      </div>
                    </div>

                    <div class="findingo-chart-wrap">
                      <product-chart
                        class="findingo-chart"
                        :key="getTopChartKey(card)"
                        :product="getTopChartProduct(card)"
                      />
                    </div>

                    <div class="findingo-card-bottom">
                      <div class="findingo-card-title-row">
                        <div class="findingo-card-title-content">
                          <h3 class="findingo-card-title">
                            {{ card.product.amazonNamenSuche }}
                          </h3>

                          <div class="findingo-card-trust">
                            <img
                              src="/wp-content/plugins/tv-finder/amazon_icon.png"
                              alt="Amazon"
                              class="findingo-card-amazon-img"
                            />
                            <span class="findingo-shop-label">bei Amazon.de</span>
                          </div>
                        </div>

                        <div class="findingo-card-price-inline">
                          <div class="findingo-card-price">
                            {{ getPriceLabel(card.product) }}
                          </div>

                          <div class="findingo-card-price-source">
                            <span>
                              Aktualisiert: {{ card.product.updated_time || "heute" }}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div class="findingo-card-actions">
                        <a
                          :href="card.product.amazon_url || '#'"
                          target="_blank"
                          rel="sponsored nofollow noopener"
                          class="findingo-card-button findingo-card-button--primary"
                        >
                          Zum Angebot*
                          <i class="bi bi-arrow-up-right"></i>
                        </a>

                        <button
                          type="button"
                          class="findingo-card-button findingo-card-button--secondary"
                          @click="flipTopCard(card.type)"
                        >
                          Details vergleichen
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="findingo-card-back">
                <div class="findingo-card-back-inner">
                  <TopThreeDatenblattMod
                    :key="'datenblatt-' + getCardInstanceId(card)"
                    :product="card.product"
                    :instance-id="'datenblatt-' + getCardInstanceId(card)"
                  />

                  <button
                    type="button"
                    class="findingo-datasheet-button findingo-card-back-button"
                    @click="flipTopCard(card.type)"
                  >
                    <i class="fa-solid fa-rotate-left findingo-icon-margin-right"></i>
                    Zurück zur Übersicht
                  </button>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>

      <div class="col-12 col-xxl-3 d-flex justify-content-center">
        <aside class="findingo-sidebar">
          <div class="findingo-sidebar-inner text-center">
            <div class="card shadow-sm mb-4 findingo-reason-card">
              <div class="row g-0">
                <div class="col-12 col-md-8 col-xxl-12">
                  <div class="card-body">
                    <div class="d-flex align-items-center mb-3">
                      <img
                        src="/wp-content/plugins/tv-berater/logo_small.png"
                        alt="Findingo"
                        class="findingo-reason-logo rounded-circle me-3"
                      >

                      <h4 class="h6 mb-0 text-secondary fw-semibold text-start">
                        Wieso diese Heißluftfritteusen zu dir passen?
                      </h4>
                    </div>

                    <p class="text-muted small mb-2 text-start">
                      Deine persönliche Auswahl
                    </p>

                    <div v-if="explanations && explanations.length" class="d-grid gap-3">
                      <div
                        v-for="(text, idx) in explanations"
                        :key="idx"
                        class="p-3 rounded shadow-sm bg-success bg-opacity-10 hover-shadow transition"
                      >
                        <p class="findingo-recommendation-text small mb-0">
                          {{ text }}
                        </p>
                      </div>
                    </div>

                    <div v-else class="p-3 rounded shadow-sm bg-success bg-opacity-10">
                      <p class="findingo-recommendation-text small mb-0">
                        Deine Empfehlungen wurden anhand deiner Antworten und der passenden Produktdaten ausgewählt.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="card findingo-share-card">
              <div class="card-body text-center">
                <h3 class="h6 mb-3">
                  Ergebnis teilen
                </h3>

                <p class="text-muted small mb-4">
                  Teile jetzt deine persönliche Heißluftfritteusen-Auswahl mit Freunden, Bekannten oder Kollegen!
                </p>

                <div class="mt-1 mb-3 d-flex justify-content-center gap-3">
                  <a :href="whatsappUrl" target="_blank" rel="noopener">
                    <i class="bi bi-whatsapp findingo-share-icon findingo-share-icon--whatsapp"></i>
                  </a>

                  <a :href="redditUrl" target="_blank" rel="noopener">
                    <i class="bi bi-reddit findingo-share-icon findingo-share-icon--reddit"></i>
                  </a>

                  <a :href="twitterUrl" target="_blank" rel="noopener">
                    <i class="bi bi-twitter findingo-share-icon findingo-share-icon--twitter"></i>
                  </a>

                  <a :href="facebookUrl" target="_blank" rel="noopener">
                    <i class="bi bi-facebook findingo-share-icon findingo-share-icon--facebook"></i>
                  </a>

                  <a :href="emailUrl">
                    <i class="bi bi-envelope findingo-share-icon findingo-share-icon--email"></i>
                  </a>

                  <div class="position-relative d-inline-block">
                    <div
                      v-if="copied"
                      class="findingo-copy-notification position-absolute top-0 start-50 translate-middle-x bg-dark text-white px-2 py-1 rounded"
                    >
                      Link zum Teilen kopiert!
                    </div>

                    <button type="button" @click="copyLink" class="btn p-0">
                      <i class="bi bi-clipboard findingo-share-icon findingo-share-icon--clipboard"></i>
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  @click="$emit('reset')"
                  class="findingo-datasheet-button"
                >
                  Neue Beratung starten
                </button>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </div>
</template>

<script>
import TopThreeDatenblattMod from "./TopThreeDatenblattMod.vue";
import PicCarousel from "./PicCarousel.vue";
import ProductChart from "./ProductChart.vue";

export default {
  components: {
    TopThreeDatenblattMod,
    PicCarousel,
    ProductChart,
  },

  props: {
    lowestPrice: { type: Object, required: true },
    bestRating: { type: Object, required: true },
    bestAmazon: { type: Object, required: true },
    explanations: { type: Array, default: () => [] },
    shareableLink: { type: String, default: null },
  },

  emits: [
    "generate-share",
    "copy-share",
    "reset",
  ],

  data() {
    return {
      copied: false,
      _timeoutId: null,
      flippedTopCards: [],
    };
  },

  mounted() {
    if (!this.shareableLink) {
      this.$emit("generate-share");
    }
  },

  beforeUnmount() {
    if (this._timeoutId) {
      clearTimeout(this._timeoutId);
    }
  },

  computed: {
    topCards() {
      const baseCards = [
        {
          type: "lowest",
          ribbon: "Günstigster Preis",
          icon: "fa-solid fa-tag",
          product: this.lowestPrice,
        },
        {
          type: "rating",
          ribbon: "Höchste Bewertung",
          icon: "fa-regular fa-star",
          product: this.bestRating,
        },
        {
          type: "amazon",
          ribbon: "Preis-Leistungssieger",
          icon: "fa-solid fa-scale-balanced",
          product: this.bestAmazon,
        },
      ].filter((card) => card.product);

      return baseCards.reduce((cards, card) => {
        const productId = this.getProductId(card.product);
        const mergeKey = productId || `fallback-${card.type}`;

        const badge = {
          type: card.type,
          ribbon: card.ribbon,
          icon: card.icon,
        };

        const existingCard = cards.find((item) => item.mergeKey === mergeKey);

        if (existingCard) {
          existingCard.badges.push(badge);
          return cards;
        }

        cards.push({
          type: card.type,
          product: card.product,
          mergeKey,
          badges: [badge],
        });

        return cards;
      }, []);
    },

    twitterUrl() {
      const url = encodeURIComponent(this.shareableLink || "");
      const text = encodeURIComponent("Schau dir meine Heißluftfritteusen-Empfehlungen an!");
      return `https://twitter.com/intent/tweet?url=${url}&text=${text}`;
    },

    whatsappUrl() {
      const msg = encodeURIComponent(
        "Schau dir meine Heißluftfritteusen-Empfehlungen an: " + (this.shareableLink || "")
      );
      return `https://api.whatsapp.com/send?text=${msg}`;
    },

    facebookUrl() {
      const url = encodeURIComponent(this.shareableLink || "");
      return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    },

    redditUrl() {
      const url = encodeURIComponent(this.shareableLink || "");
      const title = encodeURIComponent("Meine Heißluftfritteusen-Empfehlungen");
      return `https://www.reddit.com/submit?url=${url}&title=${title}`;
    },

    emailUrl() {
      const subject = encodeURIComponent("Heißluftfritteusen-Empfehlungen für dich");
      const body = encodeURIComponent(
        `Hallo,

schau dir mal diese Heißluftfritteusen-Auswahl an:
${this.shareableLink || ""}

Viele Grüße!`
      );

      return `mailto:?subject=${subject}&body=${body}`;
    },
  },

  methods: {
    getCombinedBadgeTitle(badges) {
      const count = badges?.length || 0;

      if (count === 2) return "Doppelsieger";
      if (count === 3) return "Triplesieger";
      if (count > 3) return "Mehrfachsieger";

      return "";
    },

    getBadgeShortLabel(type) {
      const labels = {
        lowest: "Preis",
        rating: "Bewertung",
        amazon: "Preis-Leistung",
      };

      return labels[type] || "";
    },

    getSafeChartId(value) {
      return String(value || "").replace(/[^a-zA-Z0-9_-]/g, "-");
    },

    getTopChartProduct(card) {
      return {
        ...card.product,
        id: this.getSafeChartId(this.getCardInstanceId(card)),
      };
    },

    getTopChartKey(card) {
      return [
        "top-chart",
        this.getSafeChartId(this.getCardInstanceId(card)),
        card.product?.frittierung_pred ?? "",
        card.product?.vielseitigkeit_pred ?? "",
        card.product?.grosse_menge_pred ?? "",
        card.product?.mixed_pred ?? "",
      ].join("-");
    },

    getProductId(product) {
      return String(
        product?.id ??
        product?.asin ??
        product?.amazon_url ??
        product?.amazonNamenSuche ??
        ""
      );
    },

    getCardInstanceId(card) {
      return `top-three-${card.type}-${this.getProductId(card.product)}`;
    },

    getTopSpecs(product) {
      const length = product?.tiefe_mm;
      const width = product?.breite_mm;
      const height = product?.hoehe_mm;

      const dimensions =
        Number(length) && Number(width) && Number(height)
          ? `${length}×${width}×${height}`
          : "keine Angabe";

      const tempMin = product?.temperatur_min_c;
      const tempMax = product?.temperatur_max_c;

      const temperature =
        tempMin !== null &&
        tempMin !== undefined &&
        tempMin !== "" &&
        tempMax !== null &&
        tempMax !== undefined &&
        tempMax !== ""
          ? `${tempMin}°C - ${tempMax}°C`
          : "keine Angabe";

      return [
        {
          label: "Maße in mm (LxBxH)",
          value: dimensions,
        },
        {
          label: "Fassungsvermögen",
          value: Number(product?.fassungsvermoegen_l)
            ? `${product.fassungsvermoegen_l} Liter`
            : "keine Angabe",
        },
        {
          label: "Temperaturbereich",
          value: temperature,
        },
        {
          label: "Leistung",
          value: Number(product?.leistung_w)
            ? `${product.leistung_w} Watt`
            : "keine Angabe",
        },
      ];
    },

    getPriceLabel(product) {
      const price = this.formatPreis(product?.preisAmazonNeu);
      return price ? `${price} €` : "Preis prüfen";
    },

    async copyLink() {
      try {
        const url = this.shareableLink || window.location.href;
        await navigator.clipboard.writeText(url);

        this.copied = true;

        if (this._timeoutId) {
          clearTimeout(this._timeoutId);
        }

        this._timeoutId = setTimeout(() => {
          this.copied = false;
          this._timeoutId = null;
        }, 2000);

        this.$emit("copy-share", url);
      } catch (err) {
        console.error("Link konnte nicht kopiert werden:", err);
      }
    },

    formatPreis(preis) {
      const parsedPrice = Number.parseFloat(String(preis ?? "").replace(",", "."));

      if (!Number.isFinite(parsedPrice)) {
        return "";
      }

      return parsedPrice.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    flipTopCard(type) {
      const id = String(type);
      const cardIndex = this.flippedTopCards.indexOf(id);

      if (cardIndex === -1) {
        this.flippedTopCards.push(id);
      } else {
        this.flippedTopCards.splice(cardIndex, 1);
      }
    },

    isTopCardFlipped(type) {
      return this.flippedTopCards.includes(String(type));
    },
  },
};
</script>

<style scoped>
.findingo-top3 {
  --top-border: rgba(28, 35, 48, 0.1);
  --top-text: #111827;
  --top-muted: #6b7280;
  --top-shadow-soft: 0 18px 40px rgba(17, 24, 39, 0.09);
}

.findingo-shop-label {
  font-size: smaller;
}

.findingo-results-hero {
  margin-bottom: 22px;
}

.findingo-results-hero-content {
  max-width: 980px;
}

.findingo-results-title {
  margin: 0;
  color: var(--top-text);
  font-size: clamp(1.55rem, 2.2vw, 2.25rem);
  line-height: 1.1;
  font-weight: 900;
  letter-spacing: -0.04em;
}

.findingo-results-subtitle {
  margin: 9px 0 0;
  color: #667085;
  font-size: clamp(0.95rem, 1.1vw, 1.08rem);
  line-height: 1.45;
  font-weight: 650;
}

.findingo-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  align-items: stretch;
  padding-top: 22px;
}

.findingo-grid-item {
  min-width: 0;
}

.findingo-product-card {
  position: relative;
  transform-style: preserve-3d;
  transition: transform 0.55s ease;
  border: 0;
  background: transparent;
}

.findingo-product-card .findingo-card-front,
.findingo-product-card .findingo-card-back {
  width: 100%;
  backface-visibility: hidden;
  border-radius: 22px;
}

.findingo-product-card .findingo-card-front {
  position: relative;
  overflow: visible;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(255, 253, 249, 0.94));
  border: 1px solid var(--top-border);
  box-shadow: var(--top-shadow-soft);
}

.findingo-product-card .findingo-card-back {
  position: absolute;
  inset: 0;
  overflow: auto;
  background: #ffffff;
  border: 1px solid var(--top-border);
  transform: rotateY(180deg);
}

.findingo-product-card.findingo-is-flipped {
  transform: rotateY(180deg);
}

.findingo-product-card--lowest .findingo-card-front {
  border-color: rgba(79, 189, 117, 0.36);
}

.findingo-product-card--rating .findingo-card-front {
  border-color: rgba(216, 177, 74, 0.38);
}

.findingo-product-card--amazon .findingo-card-front {
  border-color: rgba(75, 131, 188, 0.32);
}

.findingo-badge {
  position: absolute;
  top: -16px;
  left: -14px;
  z-index: 50;
  isolation: isolate;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-width: 205px;
  max-width: calc(100% - 22px);
  min-height: 52px;
  padding: 0 20px 0 18px;
  color: #273244;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.985), rgba(252, 250, 246, 0.955));
  border: 1px solid rgba(28, 35, 48, 0.11);
  border-radius: 10px 10px 10px 0;
  box-shadow:
    0 14px 24px rgba(17, 24, 39, 0.11),
    0 4px 8px rgba(17, 24, 39, 0.07),
    inset 0 1px 0 rgba(255, 255, 255, 0.94);
  pointer-events: none;
}

.findingo-badge::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: -10px;
  z-index: -1;
  width: 14px;
  height: 10px;
  background: currentColor;
  clip-path: polygon(0 0, 100% 0, 100% 100%);
  opacity: 0.95;
}

.findingo-badge::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0;
  background:
    linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.72) 0%,
      rgba(255, 255, 255, 0.16) 44%,
      rgba(255, 255, 255, 0) 100%
    );
  border-radius: inherit;
  pointer-events: none;
}

.findingo-badge__icon {
  position: relative;
  z-index: 2;
  display: inline-flex;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: currentColor;
  font-size: 19px;
  line-height: 1;
}

.findingo-badge__text {
  position: relative;
  z-index: 2;
  min-width: 0;
  color: currentColor;
  font-size: 14px;
  font-weight: 850;
  line-height: 1;
  letter-spacing: -0.012em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.findingo-badge--lowest {
  color: #15834f;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.99), rgba(246, 255, 249, 0.96));
  border-color: rgba(21, 131, 79, 0.24);
}

.findingo-badge--rating {
  color: #c47a00;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.99), rgba(255, 250, 241, 0.96));
  border-color: rgba(196, 122, 0, 0.24);
}

.findingo-badge--amazon {
  color: #2674d9;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.99), rgba(245, 249, 255, 0.96));
  border-color: rgba(38, 116, 217, 0.24);
}

.findingo-badge--combined {
  justify-content: flex-start;
  gap: 13px;
  width: max-content;
  min-width: 252px;
  max-width: calc(100% + 18px);
  min-height: 62px;
  padding: 8px 20px 8px 18px;
  color: #c47a00;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.99), rgba(255, 250, 241, 0.96));
  border-color: rgba(196, 122, 0, 0.24);
}

.findingo-badge__icon--combined {
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  color: #c47a00;
  font-size: 21px;
}

.findingo-badge__content {
  position: relative;
  z-index: 2;
  display: grid;
  gap: 3px;
  min-width: 0;
}

.findingo-badge__headline {
  display: block;
  color: #2a241c;
  font-size: 17.5px;
  font-weight: 900;
  line-height: 1.05;
  letter-spacing: -0.025em;
  white-space: nowrap;
}

.findingo-badge__subline {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  color: #667085;
  font-size: 12.5px;
  font-weight: 750;
  line-height: 1.1;
  white-space: nowrap;
}

.findingo-badge__subitem {
  display: inline-flex;
  align-items: center;
}

.findingo-badge__subitem:not(:last-child)::after {
  content: "•";
  margin: 0 7px;
  color: rgba(102, 112, 133, 0.72);
  font-weight: 900;
}

.findingo-card-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 54px 18px 18px;
}

.findingo-card-media {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 214px;
  margin: 0 0 18px;
}

.findingo-card-media :deep(.splide) {
  width: 100%;
  max-width: 250px;
  margin-right: auto;
  margin-left: auto;
}

.findingo-card-media :deep(img) {
  object-fit: contain;
}

.findingo-card-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.findingo-card-info-layout {
  display: grid;
  grid-template-columns: 1fr;
  align-items: stretch;
  margin: 0 0 18px;
}

.findingo-card-specs {
  display: grid;
  grid-template-columns: max-content max-content;
  justify-content: space-between;
  align-items: start;
  width: 100%;
  max-width: 354px;
  margin-right: auto;
  margin-left: auto;
  column-gap: 24px;
  row-gap: 12px;
}

.findingo-card-spec {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
  min-width: 0;
  color: #273244;
}

.findingo-card-spec > span {
  min-width: 0;
}

.findingo-card-spec strong {
  display: block;
  color: #070708;
  line-height: 1.25;
  white-space: nowrap;
}

.findingo-card-spec small {
  display: block;
  margin-top: 1px;
  color: var(--top-muted);
  font-size: 15px;
  line-height: 1.2;
}

.findingo-chart-wrap {
  width: 100%;
  max-width: 354px;
  height: 104px;
  min-height: 104px;
  margin: 0 auto 18px;
}

.findingo-chart-wrap :deep(.findingo-chart),
.findingo-chart-wrap :deep(.findingo-score-chart) {
  position: relative;
  width: 100%;
  height: 104px;
  min-height: 104px;
}

.findingo-chart-wrap :deep(canvas) {
  display: block;
  width: 100% !important;
  height: 104px !important;
}

.findingo-card-bottom {
  display: grid;
  gap: 14px;
  margin-top: auto;
  padding-top: 0;
}

.findingo-card-title-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: end;
  min-height: 42px;
  margin-top: 0;
}

.findingo-card-title-content {
  min-width: 0;
}

.findingo-card-title {
  margin: 0;
  color: var(--top-text);
  font-size: 17.5px;
  font-weight: 850;
  line-height: 1.18;
  letter-spacing: -0.025em;
}

.findingo-card-trust {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}

.findingo-card-amazon-img {
  display: block;
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.findingo-reason-logo {
  width: 40px;
  height: 40px;
  border: 2px solid #ccc;
}

.findingo-recommendation-text {
  color: #15803d;
}

.findingo-copy-notification {
  white-space: nowrap;
  z-index: 20;
  font-size: 11px;
}

.findingo-card-price-inline {
  display: grid;
  justify-items: end;
  gap: 5px;
  min-width: 96px;
  padding-top: 1px;
}

.findingo-card-price {
  color: #222;
  font-size: 17.5px;
  font-weight: 900;
  line-height: 1.05;
  letter-spacing: -0.035em;
  text-align: right;
  white-space: nowrap;
}

.findingo-card-price-source {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  color: #7a8494;
  font-size: 9.5px;
  font-weight: 650;
  line-height: 1;
  white-space: nowrap;
}

.findingo-card-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.findingo-card-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 42px;
  color: #222;
  font-size: 12.5px;
  font-weight: 800;
  line-height: 1;
  text-decoration: none;
  border: 1px solid #222;
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease,
    border-color 0.18s ease;
}

.findingo-card-button:hover {
  text-decoration: none;
  transform: translateY(-1px);
}

.findingo-card-button--primary {
  background: #f8d4a8;
}

.findingo-card-button--primary:hover {
  box-shadow: 0 14px 26px rgba(35, 74, 49, 0.26);
}

.findingo-card-button--secondary {
  color: #273244;
  background: #ffffff;
  border-color: rgba(17, 24, 39, 0.14);
}

.findingo-card-button--secondary:hover {
  background: #faf8f4;
  border-color: rgba(17, 24, 39, 0.22);
}

.findingo-card-back-inner {
  min-height: 100%;
  padding: 18px;
  background: #ffffff;
}

.findingo-card-back-button {
  margin-top: 14px;
}

.findingo-datasheet-button {
  padding: 8px 14px;
  color: #273244;
  font-size: 13px;
  font-weight: 750;
  background: #ffffff;
  border: 1px solid rgba(17, 24, 39, 0.18);
  border-radius: 11px;
  transition: background-color 0.18s ease, transform 0.18s ease;
}

.findingo-datasheet-button:hover {
  background: #faf8f4;
  transform: translateY(-1px);
}

.findingo-icon-margin-right {
  margin-right: 8px;
}

.findingo-sidebar {
  width: 100%;
  max-width: 380px;
}

.findingo-sidebar .findingo-sidebar-inner {
  margin-top: 5px;
}

.findingo-reason-card,
.card.findingo-share-card {
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 18px;
}

.card.findingo-share-card {
  background: linear-gradient(
    120deg,
    #fcf5eb2e 0%,
    #f0ebe5 50%,
    #e6e1dc8f 100%
  );
}

.findingo-share-icon {
  font-size: 1.5rem;
  transition: opacity 0.2s;
}

.findingo-share-icon:hover {
  opacity: 0.8;
}

.findingo-share-icon--twitter {
  color: #1DA1F2;
}

.findingo-share-icon--facebook {
  color: #1877F2;
}

.findingo-share-icon--whatsapp {
  color: #25D366;
}

.findingo-share-icon--reddit {
  color: #FF4500;
}

.findingo-share-icon--email {
  color: #EA4335;
}

.findingo-share-icon--clipboard {
  color: #6c757d;
}

@media (max-width: 1399.98px) {
  .findingo-card-inner {
    padding: 54px 16px 18px;
  }

  .findingo-card-media {
    min-height: 210px;
    margin-bottom: 17px;
  }

  .findingo-product-card,
  .findingo-product-card .findingo-card-front,
  .findingo-product-card .findingo-card-back,
  .findingo-card-inner {
    min-height: 630px;
  }
}

@media (max-width: 1199.98px) {
  .findingo-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .findingo-sidebar {
    max-width: 100%;
  }
}

@media (max-width: 991.98px) {
  .findingo-results-hero {
    margin-bottom: 20px;
  }

  .findingo-results-hero-content {
    max-width: 100%;
  }
}

@media (max-width: 767.98px) {
  .findingo-grid {
    grid-template-columns: 1fr;
  }

  .findingo-product-card,
  .findingo-product-card .findingo-card-front,
  .findingo-product-card .findingo-card-back,
  .findingo-card-inner {
    min-height: auto;
  }
}

@media (max-width: 576px) {
  .findingo-results-title {
    font-size: 1.55rem;
  }

  .findingo-results-subtitle {
    font-size: 0.95rem;
  }

  .findingo-card-inner {
    padding: 52px 14px 14px;
  }

  .findingo-badge {
    top: -12px;
    left: -12px;
    gap: 10px;
    min-width: 178px;
    max-width: calc(100% - 16px);
    min-height: 46px;
    padding: 0 16px 0 15px;
    border-radius: 9px 9px 9px 0;
  }

  .findingo-badge::after {
    bottom: -8px;
    width: 12px;
    height: 8px;
  }

  .findingo-badge--combined {
    gap: 11px;
    width: max-content;
    min-width: 224px;
    max-width: calc(100% - 16px);
    min-height: 56px;
    padding: 7px 16px 7px 15px;
  }

  .findingo-badge__icon {
    flex-basis: 21px;
    width: 21px;
    height: 21px;
    font-size: 16px;
  }

  .findingo-badge__icon--combined {
    flex-basis: 23px;
    width: 23px;
    height: 23px;
    font-size: 18px;
  }

  .findingo-badge__headline {
    font-size: 15.5px;
  }

  .findingo-badge__subline {
    flex-wrap: wrap;
    row-gap: 2px;
    font-size: 11.5px;
    white-space: normal;
  }

  .findingo-badge__subitem:not(:last-child)::after {
    margin: 0 5px;
  }

  .findingo-badge__text {
    font-size: 12.5px;
  }

  .findingo-card-media {
    min-height: 174px;
    margin-bottom: 16px;
  }

  .findingo-card-media :deep(img) {
    max-height: 200px;
  }

  .findingo-card-info-layout {
    margin-bottom: 16px;
  }

  .findingo-card-specs {
    max-width: 330px;
    column-gap: 18px;
    row-gap: 11px;
  }

  .findingo-chart-wrap {
    margin-bottom: 16px;
  }

  .findingo-card-title-row {
    grid-template-columns: 1fr;
    gap: 7px;
    align-items: start;
    min-height: 0;
  }

  .findingo-card-price-inline {
    justify-items: start;
    min-width: 0;
  }

  .findingo-card-price {
    text-align: left;
  }

  .findingo-card-price-source {
    justify-content: flex-start;
  }

  .findingo-card-actions {
    grid-template-columns: 1fr;
  }

  .findingo-sidebar {
    padding: 0 1rem;
  }
}

@media (max-width: 480px) {
  .findingo-card-title-row {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
  }

  .findingo-card-title-content {
    min-width: 0;
    flex: 1 1 auto;
  }

  .findingo-card-price-inline {
    flex: 0 0 auto;
    text-align: right;
    white-space: nowrap;
    justify-items: end;
  }

  .findingo-card-price {
    text-align: right;
  }

  .findingo-card-price-source {
    justify-content: flex-end;
  }
}
</style>